"use client";

import { ChevronRightIcon, SparklesIcon } from "@heroicons/react/20/solid";
import { CircleCheckBig, Loader2, MailOpen, Trash } from "lucide-react";
import {
	type ReactNode,
	type UIEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { cn } from "../../lib/cn";
import {
	VanishList,
	VanishListItem,
	type VanishExitVariant,
} from "../custom/vanish-list";

/** Notifications / inbox: items per scroll batch (client); align with `config.userNotifications.listPageSize` in app. */
export const NOTIFICATION_LIST_PAGE_SIZE = 10;
/** Upper bound on items shown from the passed-in list (matches server storage cap). */
export const NOTIFICATION_LIST_MAX_ITEMS = 50;

/**
 * Header overlay tray / notifications panel surface — theme-adaptive, aligned with
 * UserMenu dropdown (`bg-popover` / `text-popover-foreground`).
 */
export const NOTIFICATION_TOOLTIP_SURFACE_CLASS =
	"rounded-2xl border border-border bg-popover text-popover-foreground shadow-xl shadow-black/10 dark:shadow-[0_18px_45px_rgba(0,0,0,0.45)]";

export type StackedListCardVariant = "default" | "overlay";
export type StackedListCardDensity = "default" | "compact";
/** `divided` — classic connected rows; `tiles` — separate splash-style cards with gap. */
export type StackedListCardItemChrome = "divided" | "tiles";

export interface StackedListCardItem {
	id: string;
	/** Primary line (e.g. “You have reached level 12”). */
	title: string;
	/** Secondary line under the title. */
	subtitle?: string;
	href?: string;
	/** @deprecated Prefer {@link StackedListCardItem.timeAgoLabel}. */
	timeLabel?: string;
	/** Italic relative time (e.g. “5 minutes ago”); shown on the subtitle row, right side. */
	timeAgoLabel?: string;
	timeDateTime?: string;
	imageUrl?: string;
	imageAlt?: string;
	/** When true, row shows an “unread” affordance in the meta column. */
	unread?: boolean;
	/** True for a short duration after marking read to show a green check flash. */
	recentlyMarkedRead?: boolean;
	/** When true, disable + show spinner for the mark-read affordance. */
	markReadPending?: boolean;
	/** When true, disable + show spinner for the delete affordance. */
	deletePending?: boolean;
}

export interface StackedListCardProps {
	items: readonly StackedListCardItem[];
	className?: string;
	/** `"overlay"` — flush panel list (header trays / notifications); theme-adaptive like UserMenu. */
	variant?: StackedListCardVariant;
	/**
	 * `"compact"` — smaller type, fixed 1-line title / 2-line subtitle heights
	 * (header notifications tray).
	 */
	density?: StackedListCardDensity;
	/**
	 * `divided` (default) — connected rows with hairline dividers.
	 * `tiles` — separate splash tiles with vertical gap (notifications tray).
	 */
	itemChrome?: StackedListCardItemChrome;
	/**
	 * When set, rows exit via {@link VanishListItem} (AnimatePresence).
	 * Notifications delete uses `"destructive"`.
	 * Remount the list (e.g. `key={page}`) when paging so exits only run on delete.
	 */
	vanishExitVariant?: VanishExitVariant;
	onItemActivate?: (item: StackedListCardItem) => void;
	/** Optional: called when the unread dot / check affordance is clicked. */
	onItemMarkRead?: (item: StackedListCardItem) => void;
	/** Optional: called when the trash button is clicked. */
	onItemDelete?: (item: StackedListCardItem) => void;
}

/**
 * Tailwind Plus–style stacked rows in a rounded card (dividers, hover).
 * Use for notification lists, recent activity, etc.
 */
export function StackedListCard({
	items,
	className,
	variant = "default",
	density = "default",
	itemChrome = "divided",
	vanishExitVariant,
	onItemActivate,
	onItemMarkRead,
	onItemDelete,
}: StackedListCardProps) {
	const isOverlay = variant === "overlay";
	const isTiles = itemChrome === "tiles";
	const listClassName = cn(
		isTiles
			? "flex flex-col gap-2 bg-transparent shadow-none outline-none"
			: isOverlay
				? "divide-y divide-border/50 bg-transparent shadow-none outline-none"
				: "divide-y divide-gray-100 bg-white shadow-xs outline-1 outline-gray-900/5 sm:rounded-xl dark:divide-white/5 dark:bg-gray-800/50 dark:shadow-none dark:outline-white/10 dark:sm:-outline-offset-1",
		className,
	);

	const tileShellClass = cn(
		"overflow-hidden rounded-xl p-0 shadow-none backdrop-blur-md",
		"bg-background/70 text-foreground ring-1 ring-inset ring-border",
	);

	const rows = items.map((item) => {
		const row = (
			<StackedListCardRow
				item={item}
				variant={variant}
				density={density}
				omitListItem={Boolean(vanishExitVariant) || isTiles}
				onActivate={onItemActivate}
				onMarkRead={onItemMarkRead}
				onDelete={onItemDelete}
			/>
		);

		const tiled = isTiles ? (
			<div className={tileShellClass} data-stacked-list-tile="">
				{row}
			</div>
		) : (
			row
		);

		if (!vanishExitVariant) {
			if (isTiles) {
				return (
					<li key={item.id} className="list-none">
						{tiled}
					</li>
				);
			}
			return (
				<StackedListCardRow
					key={item.id}
					item={item}
					variant={variant}
					density={density}
					onActivate={onItemActivate}
					onMarkRead={onItemMarkRead}
					onDelete={onItemDelete}
				/>
			);
		}

		return (
			<VanishListItem
				key={item.id}
				as="li"
				exitVariant={vanishExitVariant}
				className="list-none"
			>
				{tiled}
			</VanishListItem>
		);
	});

	if (vanishExitVariant) {
		return (
			<VanishList
				as="ul"
				className={listClassName}
				data-stacked-list-vanish=""
			>
				{rows}
			</VanishList>
		);
	}

	return <ul className={listClassName}>{rows}</ul>;
}

export interface StackedListCardLazyProps
	extends Omit<StackedListCardProps, "items"> {
	items: readonly StackedListCardItem[];
	/** Optional sticky header row rendered inside the scroll container. */
	stickyHeader?: ReactNode;
	/** Remove horizontal padding so cards are flush to the container edges. */
	flushHorizontal?: boolean;
	/** Items per “page” when scrolling. Default: {@link NOTIFICATION_LIST_PAGE_SIZE}. */
	pageSize?: number;
	/** Max items taken from `items` (e.g. server cap). Default: {@link NOTIFICATION_LIST_MAX_ITEMS}. */
	maxItems?: number;
	/** Scrollable wrapper; put max-height here. */
	scrollContainerClassName?: string;
	/** Optional: load more from server (cursor). When set, called when user nears bottom and `hasMoreFromServer` is true. */
	onRequestMore?: () => void | Promise<void>;
	/** When using `onRequestMore`, set true while fetching. */
	isLoadingMore?: boolean;
	/** Server has more rows not yet in `items`. */
	hasMoreFromServer?: boolean;
}

/**
 * Renders {@link StackedListCard} inside a scroll area: shows `pageSize` rows initially, then loads more in batches when the user scrolls to the bottom (and optional intersection sentinel), up to `maxItems` or `items.length`.
 */
export function StackedListCardLazy({
	items,
	className,
	variant = "default",
	density = "default",
	onItemActivate,
	onItemMarkRead,
	onItemDelete,
	stickyHeader,
	flushHorizontal = false,
	pageSize = NOTIFICATION_LIST_PAGE_SIZE,
	maxItems = NOTIFICATION_LIST_MAX_ITEMS,
	scrollContainerClassName,
	onRequestMore,
	isLoadingMore = false,
	hasMoreFromServer = false,
}: StackedListCardLazyProps) {
	const cap = Math.min(items.length, maxItems);
	const [visibleCount, setVisibleCount] = useState(pageSize);
	const prevCapRef = useRef(cap);
	const sentinelRef = useRef<HTMLDivElement | null>(null);
	const scrollRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (cap < prevCapRef.current) {
			setVisibleCount((c) => Math.min(c, cap));
		}
		prevCapRef.current = cap;
	}, [cap]);

	const nShown = Math.min(visibleCount, cap);
	const visibleItems = useMemo(() => items.slice(0, nShown), [items, nShown]);

	const canRevealMoreLocal = nShown < cap;
	const canRequestServer =
		Boolean(onRequestMore) && hasMoreFromServer && !isLoadingMore;
	const showSentinel =
		canRevealMoreLocal || canRequestServer || isLoadingMore;

	const revealNextBatch = useCallback(() => {
		setVisibleCount((c) => Math.min(c + pageSize, cap));
	}, [cap, pageSize]);

	const tryLoadMore = useCallback(() => {
		if (canRevealMoreLocal) {
			revealNextBatch();
			return;
		}
		if (canRequestServer) {
			void onRequestMore?.();
		}
	}, [canRevealMoreLocal, canRequestServer, onRequestMore, revealNextBatch]);

	const onScroll = useCallback(
		(event: UIEvent<HTMLDivElement>) => {
			const t = event.currentTarget;
			const thresholdPx = 64;
			if (t.scrollHeight - t.scrollTop - t.clientHeight > thresholdPx) {
				return;
			}
			tryLoadMore();
		},
		[tryLoadMore],
	);

	useEffect(() => {
		const root = scrollRef.current;
		const target = sentinelRef.current;
		if (!root || !target || !showSentinel) {
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				for (const e of entries) {
					if (e.isIntersecting) {
						tryLoadMore();
					}
				}
			},
			{ root, rootMargin: "0px 0px 80px 0px", threshold: 0 },
		);
		observer.observe(target);
		return () => observer.disconnect();
	}, [showSentinel, tryLoadMore, visibleItems.length, items.length]);

	return (
		<div
			ref={scrollRef}
			className={cn(
				"overflow-y-auto overscroll-contain",
				flushHorizontal ? "px-0" : "px-1.5",
				scrollContainerClassName,
			)}
			onScroll={onScroll}
		>
			{stickyHeader ? (
				<div
					className={cn(
						"sticky top-0 z-20 box-border flex h-12 shrink-0 items-center border-b border-border/50 bg-background/55 px-3 backdrop-blur-md sm:px-4",
						flushHorizontal ? "mx-0" : "-mx-1.5",
					)}
				>
					{stickyHeader}
				</div>
			) : null}
			<StackedListCard
				className={className}
				variant={variant}
				density={density}
				items={visibleItems}
				onItemActivate={onItemActivate}
				onItemMarkRead={onItemMarkRead}
				onItemDelete={onItemDelete}
			/>
			{showSentinel ? (
				<div
					ref={sentinelRef}
					className="flex min-h-6 items-center justify-center py-2"
					aria-hidden
				>
					{isLoadingMore ? (
						<span className="text-xs text-muted-foreground">
							Loading…
						</span>
					) : null}
				</div>
			) : null}
		</div>
	);
}

function StackedListCardRow({
	item,
	variant: _variant = "default",
	density = "default",
	omitListItem = false,
	onActivate,
	onMarkRead,
	onDelete,
}: {
	item: StackedListCardItem;
	variant?: StackedListCardVariant;
	density?: StackedListCardDensity;
	/** When true, skip outer `<li>` (parent is {@link VanishListItem}). */
	omitListItem?: boolean;
	onActivate?: (item: StackedListCardItem) => void;
	onMarkRead?: (item: StackedListCardItem) => void;
	onDelete?: (item: StackedListCardItem) => void;
}) {
	const isCompact = density === "compact";
	const isUnread = Boolean(item.unread);
	const rowClass = cn(
		"group relative flex w-full items-center justify-between gap-x-1 overflow-visible text-left transition-colors",
		isCompact
			? "gap-x-2 py-2.5 pl-3 pr-2 sm:pl-3.5 sm:pr-3"
			: "py-5 pl-4 pr-3 sm:pl-6 sm:pr-4",
		"hover:bg-muted/60 dark:hover:bg-white/5",
	);

	const timeRight = item.timeAgoLabel ?? item.timeLabel;
	const showSecondRow = Boolean(item.subtitle || timeRight);

	const showMarkRead = Boolean(onMarkRead);
	const showDelete = Boolean(onDelete);
	const showMetaActions = showMarkRead || showDelete;

	const titleClass = cn(
		"min-w-0 flex-1 truncate transition-[color,font-weight] duration-300 ease-out",
		isCompact ? "min-h-4 text-xs leading-4" : "text-sm/6",
		isUnread
			? "font-black text-foreground"
			: "font-medium text-muted-foreground",
	);
	const subtitleClass = cn(
		"min-w-0 flex-1 transition-[color,font-weight] duration-300 ease-out",
		isCompact
			? "line-clamp-2 min-h-8 text-[11px] leading-4"
			: "text-xs/5",
		isUnread
			? "font-semibold text-foreground/80"
			: "font-normal text-muted-foreground",
	);

	const content = (
		<div className="flex min-w-0 flex-1 items-center gap-x-3 overflow-visible sm:gap-x-4">
			{item.imageUrl ? (
				<span
					className={cn(
						"relative flex shrink-0 items-center justify-center overflow-visible self-center",
						isCompact ? "size-8" : "h-12 w-12",
					)}
				>
					<span
						aria-hidden
						className="pointer-events-none absolute inset-0 rounded-full bg-muted ring-1 ring-inset ring-border"
					/>
					<img
						alt={item.imageAlt ?? ""}
						src={item.imageUrl}
						className={cn(
							"relative z-10 shrink-0 object-contain pointer-events-none",
							isCompact ? "size-9" : "h-14 w-14",
						)}
					/>
				</span>
			) : (
				<span
					className={cn(
						"flex shrink-0 items-center justify-center self-center rounded-full bg-indigo-50 ring-1 ring-inset ring-border dark:bg-indigo-950/40",
						isCompact ? "size-8" : "size-12",
					)}
				>
					<SparklesIcon
						className={cn(
							"text-indigo-600 dark:text-indigo-400",
							isCompact ? "size-4" : "size-6",
						)}
						aria-hidden
					/>
				</span>
			)}
			<div className="min-w-0 flex-1">
				<div className="flex items-center justify-between gap-2">
					<p data-vanish-flash="" className={titleClass}>
						{item.title}
					</p>
					{showMetaActions ? (
						<div className="flex shrink-0 items-center gap-1.5">
							{showMarkRead ? (
								<button
									type="button"
									className={cn(
										"group/mark relative inline-flex items-center justify-center rounded-full transition-colors hover:bg-muted",
										isCompact ? "size-6" : "size-7",
									)}
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										if (item.markReadPending) {
											return;
										}
										onMarkRead?.(item);
									}}
									aria-label={
										item.unread
											? "Mark as read"
											: "Mark as unread"
									}
									disabled={item.markReadPending}
								>
									{item.markReadPending ? (
										<Loader2
											aria-hidden
											className="size-3.5 animate-spin text-muted-foreground"
										/>
									) : null}
									{!item.markReadPending && item.unread ? (
										<span
											aria-hidden
											className="flex size-3.5 items-center justify-center rounded-full bg-emerald-500/20 dark:bg-emerald-500/30"
										>
											<span className="size-1.5 rounded-full bg-emerald-500" />
										</span>
									) : null}
									<CircleCheckBig
										aria-hidden
										className={cn(
											"absolute left-1/2 top-1/2 z-10 size-3.5 -translate-x-1/2 -translate-y-1/2",
											item.markReadPending || item.unread
												? "opacity-0"
												: item.recentlyMarkedRead
													? "text-emerald-500 opacity-100"
													: "text-muted-foreground opacity-100",
											!item.markReadPending &&
												!item.unread
												? "group-hover/mark:opacity-0 group-focus-visible/mark:opacity-0"
												: null,
										)}
									/>
									<MailOpen
										aria-hidden
										className={cn(
											"absolute left-1/2 top-1/2 z-10 size-3.5 -translate-x-1/2 -translate-y-1/2 text-muted-foreground",
											item.markReadPending || item.unread
												? "opacity-0"
												: "opacity-0 group-hover/mark:opacity-100 group-focus-visible/mark:opacity-100",
										)}
									/>
								</button>
							) : null}
							{showDelete ? (
								<button
									type="button"
									className={cn(
										"inline-flex items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
										isCompact ? "size-6" : "size-7",
									)}
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										if (item.deletePending) {
											return;
										}
										onDelete?.(item);
									}}
									aria-label="Delete notification"
									disabled={item.deletePending}
								>
									{item.deletePending ? (
										<Loader2
											aria-hidden
											className="size-3.5 animate-spin text-muted-foreground"
										/>
									) : (
										<Trash
											aria-hidden
											className="size-3.5"
										/>
									)}
								</button>
							) : null}
						</div>
					) : null}
				</div>
				{showSecondRow ? (
					<div
						className={cn(
							"flex items-start gap-2",
							isCompact ? "mt-px" : "mt-0.5",
							item.subtitle ? "justify-between" : "justify-end",
						)}
					>
						{item.subtitle ? (
							<p
								data-vanish-flash=""
								className={subtitleClass}
							>
								{item.subtitle}
							</p>
						) : (
							<span
								className={cn(
									"min-w-0 flex-1",
									isCompact && "min-h-8",
								)}
							/>
						)}
						{timeRight ? (
							<time
								dateTime={item.timeDateTime}
								className={cn(
									"shrink-0 italic tabular-nums text-muted-foreground",
									isCompact
										? "text-[10px] leading-4"
										: "text-xs/5",
								)}
							>
								{timeRight}
							</time>
						) : null}
					</div>
				) : isCompact ? (
					<div className="mt-px min-h-8" aria-hidden />
				) : null}
			</div>
		</div>
	);

	const chevron = (
		<div
			className={cn(
				"flex shrink-0 items-center justify-center text-muted-foreground",
				isCompact ? "ml-2 w-4 self-center" : "ml-4 w-5 self-center",
			)}
			aria-hidden
		>
			<ChevronRightIcon className={isCompact ? "size-4" : "size-5"} />
		</div>
	);

	if (item.href) {
		const link = (
			<a
				href={item.href}
				className={rowClass}
				onClick={() => onActivate?.(item)}
			>
				{content}
				{chevron}
			</a>
		);
		return omitListItem ? link : <li>{link}</li>;
	}

	const allowButtonWrapper = !showMetaActions;

	const interactive = allowButtonWrapper ? (
		<button
			type="button"
			className={rowClass}
			onClick={() => onActivate?.(item)}
		>
			{content}
			{chevron}
		</button>
	) : (
		// biome-ignore lint/a11y/useSemanticElements: Row contains nested action buttons; wrapper cannot be a <button>.
		<div
			role="button"
			tabIndex={0}
			className={rowClass}
			onClick={() => onActivate?.(item)}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onActivate?.(item);
				}
			}}
		>
			{content}
			{chevron}
		</div>
	);

	return omitListItem ? interactive : <li>{interactive}</li>;
}

/**
 * Example rows for level-up: reach level N, then stat and ability points (as sent on level up).
 * Replace with API-driven `StackedListCardItem[]` when notifications are persisted.
 */
export function levelUpStackedListSampleItems(
	level: number,
): StackedListCardItem[] {
	return [
		{
			id: "level-up-reached",
			title: `You have reached level ${level}`,
			subtitle: "Your stats and rewards have been updated.",
			unread: true,
		},
		{
			id: "level-up-stat-points",
			title: "You have stat points to assign",
			subtitle: "Spend them on your core attributes.",
			unread: true,
		},
		{
			id: "level-up-ability-points",
			title: "You have ability points to assign",
			subtitle: "Improve your combat abilities.",
			unread: true,
		},
	];
}
